import gql from 'graphql-tag';
import { useQuery } from 'urql';

import { BrandEnum } from '@/globalTypes';
import { usePlayer, useQueryParamsWithTime } from '@/hooks';
import { makePagination } from '@/utils/pagination';
import {
  PlayerJackpotOptInHistoryBySourceCodeQuery,
  PlayerJackpotOptInHistoryBySourceCodeQueryVariables,
  PlayerJackpotOptInHistoryQuery,
  PlayerJackpotOptInHistoryQueryVariables,
  PlayerJackpotOptInStatusBySourceCodeQuery,
  PlayerJackpotOptInStatusBySourceCodeQueryVariables,
  PlayerJackpotOptInStatusQuery,
  PlayerJackpotOptInStatusQueryVariables,
} from './__generated__/usePlayerJackpotOptIn';
import { buildHistoryVariables } from './helpers';
import { queryParams } from './queryParams';

const STATUS_QUERY = gql`
  query PlayerJackpotOptInStatus($playerId: ID!, $brand: BrandEnum!) {
    viewer {
      id
      status: dinoJackpotOptIn(playerId: $playerId, brand: $brand)
    }
  }
`;

const STATUS_BY_SOURCE_CODE_QUERY = gql`
  query PlayerJackpotOptInStatusBySourceCode(
    $playerId: ID!
    $brand: BrandEnum!
    $sourceCode: NonEmptyString!
  ) {
    viewer {
      id
      status: dinoJackpotOptInBySourceCode(
        playerId: $playerId
        brand: $brand
        sourceCode: $sourceCode
      )
    }
  }
`;

const HISTORY_QUERY = gql`
  query PlayerJackpotOptInHistory(
    $playerId: ID!
    $brand: BrandEnum!
    $optedIn: Boolean
    $processedAt: RangeDate
    $desc: Boolean
    $before: String
    $after: String
    $first: Int
  ) {
    viewer {
      id
      history: dinoJackpotOptInHistory(
        playerId: $playerId
        brand: $brand
        optedIn: $optedIn
        processedAt: $processedAt
        desc: $desc
        before: $before
        after: $after
        first: $first
      ) {
        edges {
          node {
            optedIn
            processedAt
            initiator {
              ... on PlayerInitiator {
                player {
                  id
                  firstName
                  lastName
                  email
                }
              }
              ... on AgentInitiator {
                agent {
                  email
                  firstName
                  lastName
                }
              }
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
      }
    }
  }
`;

const HISTORY_BY_SOURCE_CODE_QUERY = gql`
  query PlayerJackpotOptInHistoryBySourceCode(
    $playerId: ID!
    $brand: BrandEnum!
    $sourceCode: NonEmptyString!
    $optedIn: Boolean
    $processedAt: RangeDate
    $desc: Boolean
    $before: String
    $after: String
    $first: Int
  ) {
    viewer {
      id
      history: dinoJackpotOptInHistoryBySourceCode(
        playerId: $playerId
        brand: $brand
        sourceCode: $sourceCode
        optedIn: $optedIn
        processedAt: $processedAt
        desc: $desc
        before: $before
        after: $after
        first: $first
      ) {
        edges {
          node {
            optedIn
            processedAt
            initiator {
              ... on PlayerInitiator {
                player {
                  id
                  firstName
                  lastName
                  email
                }
              }
              ... on AgentInitiator {
                agent {
                  email
                  firstName
                  lastName
                }
              }
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
      }
    }
  }
`;

// Backend parses playerId with UUID.fromString, so this is Player.uuid, not the route id
const usePlayerKey = () => {
  const { uuid, brand } = usePlayer();
  return { playerId: uuid, brand: brand?.code as BrandEnum | undefined };
};

export function useOptInStatus(sourceCode?: string) {
  const { playerId, brand } = usePlayerKey();
  const [{ data, fetching, error }, refresh] = useQuery<
    PlayerJackpotOptInStatusQuery | PlayerJackpotOptInStatusBySourceCodeQuery,
    | PlayerJackpotOptInStatusQueryVariables
    | PlayerJackpotOptInStatusBySourceCodeQueryVariables
  >({
    query: sourceCode ? STATUS_BY_SOURCE_CODE_QUERY : STATUS_QUERY,
    variables: { playerId, brand: brand!, sourceCode },
    pause: !playerId || !brand,
  });

  return { status: data?.viewer.status, fetching, error, refresh };
}

export function useOptInHistory() {
  const { playerId, brand } = usePlayerKey();
  const [query, setQuery] = useQueryParamsWithTime(queryParams, {
    fromAndToOptional: true,
  });
  const { bySourceCode, variables } = buildHistoryVariables({
    playerId,
    brand: brand!,
    query,
  });

  const [{ data, fetching, error }, refresh] = useQuery<
    PlayerJackpotOptInHistoryQuery | PlayerJackpotOptInHistoryBySourceCodeQuery,
    | PlayerJackpotOptInHistoryQueryVariables
    | PlayerJackpotOptInHistoryBySourceCodeQueryVariables
  >({
    query: bySourceCode ? HISTORY_BY_SOURCE_CODE_QUERY : HISTORY_QUERY,
    variables,
    pause: !playerId || !brand,
  });

  return {
    refresh,
    fetching,
    error,
    sourceCode: query.sourceCode,
    defaultFilter: query,
    updateFilter: (values: {}) => setQuery(values, 'replaceIn'),
    desc: query.desc,
    ...makePagination(query, setQuery, data?.viewer.history.pageInfo),
    history: data?.viewer.history.edges?.map((edge) => edge?.node),
  };
}
